"use client";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";


const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08 } },
  };
  
  const item = {
    hidden: { opacity: 0, y: 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
    },
  };

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    "usage",
    "cookies",
    "sharing",
    "security",
    "retention",
    "rights",
    "third_party",
    "children",
    "changes",
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-30% 0px -60% 0px",
        threshold: 0,
      }
    );

    sections.forEach((key) => {
      const el = document.getElementById(key);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  return (
        <section className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-gray-50 to-white">
            <motion.div
            className="absolute -top-40 -left-40 w-[32rem] h-[32rem] bg-indigo-400/10 blur-3xl rounded-full"
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute top-1/3 -right-40 w-[28rem] h-[28rem] bg-sky-400/10 blur-3xl rounded-full"
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
            />

            <aside className="hidden lg:block fixed top-32 left-10 w-64 z-10">
            <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-slate-400">
            Contents
            </p>

            <ul className="space-y-1 text-sm">
            {sections.map((key, i) => {
                const isActive = activeSection === key;

                return (
                <li key={key}>
                    <a
                    href={`#${key}`}
                    className={`block rounded-lg px-3 py-1.5 transition-all
                        ${
                        isActive
                            ? "bg-blue-50 text-blue-700 font-semibold"
                            : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                        }`}
                    >
                    {i + 1}. {t(`privacy.sections.${key}.title`)}
                    </a>
                </li>
                );
            })}
            </ul>
        </aside>

        <motion.div
        className="relative max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 pt-28 sm:pt-32 pb-20 text-center"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div
          variants={item}
          whileHover={{ scale: 1.05 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 mb-6
                     rounded-full bg-blue-100 text-blue-800
                     text-xs font-semibold tracking-widest uppercase"
        >
          <FileText size={14} />
          {t("terms.badge")}
        </motion.div>

        <motion.h1
          variants={item}
          className="text-4xl sm:text-5xl lg:text-6xl font-extrabold
                     tracking-tight mb-4
                     bg-gradient-to-b from-gray-200 to-slate-600
                     bg-clip-text text-transparent"
        >
          {t("privacy.title")}
        </motion.h1>

        <motion.p
          variants={item}
          className="text-slate-600 text-sm sm:text-base"
        >
          {t("privacy.last_updated")}
        </motion.p>
      </motion.div>

      {/* CONTENT */}
      <motion.div
        className="relative max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 pb-32"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <div className="space-y-8">
          {sections.map((key, index) => (
            <motion.div
              key={key}
              id={key}
              variants={item}
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
              className="rounded-3xl bg-white p-8 sm:p-10
                         shadow-sm border border-slate-200/70
                         hover:shadow-xl hover:border-blue-200/60
                         transition-shadow"
            >
              <div className="flex items-start gap-4">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center
                    rounded-full text-sm font-semibold
                    ${
                      activeSection === key
                        ? "bg-blue-600 text-white"
                        : "bg-blue-100 text-blue-700"
                    }`}
                >
                  {index + 1}
                </span>

                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">
                    {t(`privacy.sections.${key}.title`, { index: index + 1 })}
                  </h2>

                  <div className="space-y-3 text-sm sm:text-base text-slate-600 leading-relaxed">
                  {t(`privacy.sections.${key}.content`)}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* CONTACT */}
          <motion.div
            variants={item}
            className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800
                       text-white p-10 sm:p-14 shadow-2xl"
          >
            <h2 className="text-2xl font-semibold mb-4">
              {t("privacy.sections.contact.title")}
            </h2>

            <p className="text-slate-300 mb-6">
              {t("privacy.sections.contact.content")}
            </p>

            <a
              href="mailto:contact@lak-che.com"
              className="inline-flex items-center gap-2
                         rounded-full border border-white/20
                         px-5 py-2 font-semibold
                         hover:bg-white hover:text-slate-900
                         transition-colors"
            >
              <Mail size={16} />
              info@lakche.com
            </a>

            <p className="mt-4 text-sm text-slate-400">
              www.lakche.com
            </p>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}